// Choice-card option data for each step, ported verbatim from the monolith
// (OnboardingChatbotV1.vue). Titles/subs ARE the product spec (INSTALL.md §2) —
// do not paraphrase. `bubbleLabel` maps are the (shorter) text echoed back in the
// editable user bubble after a choice, distinct from the card title.

import type { ChoiceOption as UiChoiceOption } from '@twg/ui'

export interface ChoiceOption extends UiChoiceOption {
  bubbleLabel?: string
}

export interface CareTypeOption {
  key: string
  emoji: string
  label: string
  sub?: string
}

// ── Step `universe-choice` ──────────────────────────────────────
export const UNIVERSE_OPTIONS: ChoiceOption[] = [
  {
    key: 'marketplace',
    icon: new URL('../assets/universe-marketplace.png', import.meta.url).href,
    title: 'Petites annonces et mise en relation',
    sub: 'Trouvez une nounou, une garde partagée, ou encore du travail !',
    bubbleLabel: 'Petites annonces',
  },
  {
    key: 'management',
    icon: new URL('../assets/universe-management.png', import.meta.url).href,
    title: 'Une aide administrative',
    sub: 'Gérez facilement le contrat, la paie, les congés, etc.',
    bubbleLabel: 'Aide administrative',
  },
  {
    key: 'notebook',
    icon: new URL('../assets/universe-notebook.png', import.meta.url).href,
    title: 'Une app de liaison parent-nounou',
    sub: 'Partagez la journée de l\'enfant !',
    bubbleLabel: 'App de liaison',
  },
]

// ── Step `mp-role` (marketplace) ────────────────────────────────
export const MARKETPLACE_ROLE_OPTIONS: ChoiceOption[] = [
  {
    key: 'family',
    emoji: '👪',
    title: 'Je suis parent / famille',
    sub: 'Je cherche une nounou ou une garde pour mon enfant',
  },
  {
    key: 'childminder',
    emoji: '👶',
    title: 'Je suis pro de la garde d\'enfant',
    sub: 'Assistant(e) maternel(le), babysitter, garde d\'enfant, assistante parentale',
  },
  {
    key: 'other',
    emoji: '🏘️',
    title: 'Autre',
    sub: 'MAM, relais petite enfance, formateur, agence',
  },
]

// ── Step `mn-role` (management / notebook) ──────────────────────
export const MN_ROLE_OPTIONS: ChoiceOption[] = [
  { key: 'employer', emoji: '👪', title: 'Je suis employeur', sub: 'Famille, parent, particulier-employeur' },
  { key: 'employee', emoji: '👶', title: 'Je suis salarié(e)', sub: 'Assistant(e) maternel(le), babysitter, garde d\'enfant' },
  { key: 'other', emoji: '🏘️', title: 'Autre', sub: 'MAM, agence, relais petite enfance, formateur, recruteur' },
]

// ── Step `mp-caretype` (marketplace + family) ───────────────────
export const CARE_TYPE_OPTIONS: CareTypeOption[] = [
  { key: 'ponctuelle', emoji: '🌜', label: 'Garde ponctuelle', sub: 'Soirée, dépannage, mariage, week-end, semaine de vacances' },
  { key: 'partiel', emoji: '🌤️', label: 'Garde temps partiel', sub: 'Périscolaire, mercredis, aide au dîner-bain, etc.' },
  { key: 'plein', emoji: '🌞', label: 'Garde temps plein', sub: 'Enfant non scolarisé, nounou permanente' },
  { key: 'partagee', emoji: '🏡', label: 'Une autre famille', sub: 'Pour une garde partagée' },
]

// ── Step `mn-subprofile` (role === 'other') ─────────────────────
export const SUBPROFILE_OPTIONS: ChoiceOption[] = [
  { key: 'mam', emoji: '🏘️', title: 'Maison d\'assistantes maternelles' },
  { key: 'rpe', emoji: '🏛️', title: 'Relais petite enfance' },
  { key: 'trainer', emoji: '🎓', title: 'Formateur' },
  { key: 'agency', emoji: '🏢', title: 'Agence' },
]

// ── Echoed-back user-bubble labels (emoji + short label) ────────
export const UNIVERSE_BUBBLE_LABELS: Record<string, string> = {
  marketplace: 'Petites annonces',
  management: 'Aide administrative',
  notebook: 'App de liaison',
}
export const ROLE_BUBBLE_LABELS: Record<string, string> = {
  family: '👪 Parent / famille',
  childminder: '👶 Nounou / pro de la garde',
  employer: '👪 Employeur',
  employee: '👶 Salarié(e)',
  other: '🏘️ Autre',
}
export const SUBPROFILE_BUBBLE_LABELS: Record<string, string> = {
  mam: '🏘️ MAM',
  rpe: '🏛️ Relais petite enfance',
  trainer: '🎓 Formateur',
  agency: '🏢 Agence',
}
